/**
 * Simple Registration & KYC Verification API Client
 * Integration with scb-slip-verify backend
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

// Types
export interface LoginResponse {
  success: boolean;
  message: string;
  data: {
    token: string;
    refreshToken: string;
    customer: {
      id: string;
      mobile: string;
      email: string;
      shop_name: string;
      package_level: string;
    };
    expiresIn: string;
    loginAt: string;
  };
}

export interface CheckPhoneResponse {
  success: boolean;
  available: boolean;
  message: string;
  timestamp: string;
}

export interface RequestOtpResponse {
  success: boolean;
  message: string;
  data: {
    otpId: string;
    expiresAt: string;
    expiresIn: number;
    retryAfter: number;
  };
  timestamp: string;
}

export interface RegisterData {
  phoneNumber: string;
  otpCode: string;
  password: string;
  confirmPassword: string;
  shopName: string;
  bankAccount: string;
  agreeTerms: boolean;
  agreePrivacy: boolean;
  email?: string;
}

/**
 * Login with phone number and password
 */
export async function login(phoneNumber: string, password: string): Promise<LoginResponse> {
  const response = await fetch(`${API_BASE_URL}/api/auth/login/mobile`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      phoneNumber,
      password,
    }),
  });

  if (!response.ok) {
    const error: ApiError = await response.json();
    throw new Error(error.error || 'เข้าสู่ระบบไม่สำเร็จ');
  }

  return response.json();
}

export interface RegisterResponse {
  success: boolean;
  message: string;
  data: {
    user: {
      id: string;
      phoneNumber: string;
      email?: string;
      shopName: string;
      packageType: string;
      packageName: string;
      includedCredits: number;
      kycVerified: boolean;
      requiresKyc: boolean;
    };
    token: string;
    refreshToken: string;
    expiresIn: string;
  };
  timestamp: string;
}

export interface KycStatusResponse {
  success: boolean;
  data: {
    verified: boolean;
    verifiedType: 'individual' | 'company' | null;
    verifiedAt: string | null;
    kycData: any;
  };
  timestamp: string;
}

export interface IndividualKycData {
  idCard: string;
  prefix: string;
  firstName: string;
  lastName: string;
  address: string;
  subDistrict: string;
  district: string;
  province: string;
  postalCode: string;
  useAddressForTax?: boolean;
  taxInvoiceDeliveryAddress?: string;
  taxInvoiceDeliveryDistrict?: string;
  taxInvoiceDeliverySubDistrict?: string;
  taxInvoiceDeliveryProvince?: string;
  taxInvoiceDeliveryPostalCode?: string;
}

export interface CompanyKycData {
  companyName: string;
  taxId: string;
  businessType: 'corporation' | 'partnership' | 'cooperative' | 'foundation' | 'other';
  address: string;
  subDistrict: string;
  district: string;
  province: string;
  postalCode: string;
  contactPerson: string;
  useAddressForTax?: boolean;
  taxInvoiceDeliveryAddress?: string;
  taxInvoiceDeliveryDistrict?: string;
  taxInvoiceDeliverySubDistrict?: string;
  taxInvoiceDeliveryProvince?: string;
  taxInvoiceDeliveryPostalCode?: string;
}

export interface KycVerificationResponse {
  success: boolean;
  message: string;
  data: {
    verificationId: string;
    kycType: 'individual' | 'company';
    verifiedAt: string;
    fullName?: string;
    companyName?: string;
    taxId?: string;
    businessType?: string;
    address: string;
  };
  timestamp: string;
}

export interface ApiError {
  success: false;
  error: string;
  message: string;
  details?: Array<{
    field: string;
    message: string;
  }>;
  timestamp: string;
}

export interface SessionTokenResponse {
  success: boolean;
  message: string;
  data: {
    sessionToken: string;
    expiresAt: string;
    expiresIn: number;
  };
  timestamp: string;
}

export interface PackageInfo {
  id: string;
  code: string;
  name: string;
  description: string;
  price: number;
  includedCredits: number;
  qrCodeQuota?: number;
  durationDays: number;
  expiresAt: string;
  formattedExpiryDate: string;
  features: string[];
}

export interface PackageResponse {
  success: boolean;
  data: PackageInfo;
  timestamp: string;
}

export interface FinalRegistrationData {
  shopName: string;
  bankAccount?: string;
  password: string;
  confirmPassword: string;
  agreeTerms: boolean;
  agreePrivacy: boolean;
  email?: string;
  // Optional address fields (collected at registration for convenience)
  address?: string;
  province?: string;
  amphure?: string;
  tambon?: string;
  postalCode?: string;
}

/**
 * Check if phone number is available for registration
 */
export async function checkPhoneAvailability(phoneNumber: string): Promise<CheckPhoneResponse> {
  const response = await fetch(`${API_BASE_URL}/api/auth/register/check-phone`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ phoneNumber }),
  });

  if (!response.ok) {
    const error: ApiError = await response.json();
    throw new Error(error.message || 'Failed to check phone availability');
  }

  return response.json();
}

/**
 * Request OTP for registration
 */
export async function requestOtp(phoneNumber: string): Promise<RequestOtpResponse> {
  const response = await fetch(`${API_BASE_URL}/api/auth/register/resend-otp`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ phoneNumber }),
  });

  if (!response.ok) {
    const error: ApiError = await response.json();
    throw new Error(error.message || 'Failed to send OTP');
  }

  return response.json();
}

/**
 * Verify OTP code
 */
export async function verifyOtp(phoneNumber: string, otpCode: string): Promise<{ success: boolean; message: string }> {
  const response = await fetch(`${API_BASE_URL}/api/auth/register/verify-otp`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ phoneNumber, otpCode }),
  });

  if (!response.ok) {
    const error: ApiError = await response.json();
    throw new Error(error.message || 'OTP ไม่ถูกต้องหรือหมดอายุ');
  }

  return response.json();
}

/**
 * Complete simple registration
 */
export async function completeRegistration(data: RegisterData): Promise<RegisterResponse> {
  const response = await fetch(`${API_BASE_URL}/api/auth/register/simple`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error: ApiError = await response.json();
    
    // Format validation errors
    if (error.details && error.details.length > 0) {
      const errorMessages = error.details.map(d => `${d.field}: ${d.message}`).join('\n');
      throw new Error(errorMessages);
    }
    
    throw new Error(error.message || 'Registration failed');
  }

  return response.json();
}

/**
 * Get KYC verification status
 */
export async function getKycStatus(token: string): Promise<KycStatusResponse> {
  const response = await fetch(`${API_BASE_URL}/api/auth/verify/status`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error: ApiError = await response.json();
    throw new Error(error.message || 'Failed to get KYC status');
  }

  return response.json();
}

/**
 * Verify individual identity
 */
export async function verifyIndividual(
  token: string,
  data: IndividualKycData
): Promise<KycVerificationResponse> {
  const response = await fetch(`${API_BASE_URL}/api/auth/verify/individual`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error: ApiError = await response.json();
    
    // Format validation errors
    if (error.details && error.details.length > 0) {
      const errorMessages = error.details.map(d => `${d.field}: ${d.message}`).join('\n');
      throw new Error(errorMessages);
    }
    
    throw new Error(error.message || 'Individual verification failed');
  }

  return response.json();
}

/**
 * Verify company identity
 */
export async function verifyCompany(
  token: string,
  data: CompanyKycData
): Promise<KycVerificationResponse> {
  const response = await fetch(`${API_BASE_URL}/api/auth/verify/company`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error: ApiError = await response.json();
    
    // Format validation errors
    if (error.details && error.details.length > 0) {
      const errorMessages = error.details.map(d => `${d.field}: ${d.message}`).join('\n');
      throw new Error(errorMessages);
    }
    
    throw new Error(error.message || 'Company verification failed');
  }

  return response.json();
}

/**
 * Search company by tax ID
 */
export async function searchCompanyByTaxId(token: string, taxId: string): Promise<any> {
  const response = await fetch(
    `${API_BASE_URL}/api/auth/verify/company/search-tax-id?taxId=${taxId}`,
    {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    const error: ApiError = await response.json();
    throw new Error(error.message || 'Failed to search company');
  }

  return response.json();
}

/**
 * Get OTP code (development mode only)
 */
export async function getOtpForTesting(phoneNumber: string): Promise<string> {
  if (process.env.NODE_ENV === 'production') {
    throw new Error('This function is only available in development mode');
  }

  const response = await fetch(`${API_BASE_URL}/api/test/otp/${phoneNumber}`);
  
  if (!response.ok) {
    throw new Error('Failed to get OTP');
  }

  const data = await response.json();
  return data.data.otp_code;
}

/**
 * Create registration session after OTP verification
 */
export async function createRegistrationSession(
  phoneNumber: string,
  otpCode: string
): Promise<SessionTokenResponse> {
  const response = await fetch(`${API_BASE_URL}/api/auth/register/create-session`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ phoneNumber, otpCode }),
  });

  if (!response.ok) {
    const error: ApiError = await response.json();
    throw new Error(error.message || 'ไม่สามารถสร้าง session ได้');
  }

  return response.json();
}

/**
 * Get starter package information
 */
export async function getStarterPackage(): Promise<PackageResponse> {
  const response = await fetch(`${API_BASE_URL}/api/auth/register/packages/starter`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error: ApiError = await response.json();
    throw new Error(error.message || 'ไม่สามารถดึงข้อมูลแพ็กเกจได้');
  }

  return response.json();
}

/**
 * Complete registration with session token
 */
export async function completeRegistrationWithSession(
  sessionToken: string,
  data: FinalRegistrationData
): Promise<RegisterResponse> {
  const response = await fetch(`${API_BASE_URL}/api/auth/register/simple`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Session-Token': sessionToken,
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error: ApiError = await response.json();
    
    // Format validation errors
    if (error.details && error.details.length > 0) {
      const errorMessages = error.details.map(d => `${d.field}: ${d.message}`).join('\n');
      throw new Error(errorMessages);
    }
    
    throw new Error(error.message || 'Registration failed');
  }

  return response.json();
}

